
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";

export const useUpdateDeviceRules = () => {
  const queryClient = useQueryClient();
  const updateDeviceRules = useConvexMutation(api.devices.rules.update);
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      deviceId, 
      ruleId,
      enabled,
      existingDeviceRules = []
    }: { 
      deviceId: string; 
      ruleId: string;
      enabled: boolean;
      existingDeviceRules?: any[];
    }) => {
      console.log('Updating device rule:', { deviceId, ruleId, enabled });

      // Create rules object with all existing device rules plus the new/updated rule
      const rules: Record<string, boolean> = {};
      
      // Include all existing device-specific rules using their rule IDs
      existingDeviceRules.forEach((rule: any) => {
        if (rule.source === "device") {
          rules[rule.id] = true;
        }
      });

      // Add or update the current rule using the rule ID
      if (enabled) {
        rules[ruleId] = true;
      } else {
        // When disabling, we remove the rule from the rules object
        delete rules[ruleId];
      }

      console.log('Final rules object for device update:', rules);

      const result = await updateDeviceRules({
        device: deviceId as any,
        rules: rules as any
      });

      console.log('Device rule updated:', result);
      return result;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['network-rules'] });
      queryClient.invalidateQueries({ queryKey: ['device-rules', variables.deviceId] });
      
      toast({
        title: variables.enabled ? "Rule Added" : "Rule Removed",
        description: `Network rule has been ${variables.enabled ? 'added to' : 'removed from'} this device.`,
      });
    },
    onError: (error) => {
      console.error('Error updating device rule:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update device rule. Please try again.",
        variant: "destructive",
      });
    }
  });
};
